// 手机端让表单、记录与操作自然换行，移除固定最小宽度；业务提交与原有权限保持一致。
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { toast } from "../components/ui/sonner";
import { AtSign, Check, Clock, ExternalLink, CheckCircle2 } from "lucide-react";
import { fmtDT } from "../lib/date";

export default function MyMentions() {
  const { lang, api, directory, projects, user } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [onlyUnread, setOnlyUnread] = useState(true);

  const load = () => api.get("/my-mention-status").then(({ data }) => setRows(data || [])).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const nameOf = (u) => (u ? ((zh ? (u.nameZh || u.name) : (u.name || u.nameZh)) || u.username) : "");
  const dir = (un) => (directory || []).find((x) => x.username === un);
  const list = onlyUnread ? rows.filter((r) => (r.unread || []).length > 0) : rows;
  const totalUnread = rows.reduce((s, r) => s + (r.unread || []).length, 0);

  const open = (r) => {
    const p = (projects || []).find((x) => x.id === r.projectId);
    if (p) nav(`/project/${p.id}`);
  };
  const nudgeAll = async () => {
    try {
      const { data } = await api.post("/my-mentions/nudge-all");
      toast.success((zh ? "已催办 " : "Nudged ") + data.nudged + (zh ? " 条未读" : " unread"));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-5" data-testid="my-mentions-page">
      <div className="flex items-center gap-2 flex-wrap">
        <AtSign className="h-6 w-6 text-brand-500" />
        <h1 className="text-2xl font-bold text-slate-900">{zh ? "我的 @ 待读汇总" : "My @ Follow-ups"}</h1>
        <span className="text-sm text-slate-500">· {zh ? "我发出的 @ 中还有" : "unread across mine:"} <b className="text-red-600">{totalUnread}</b> {zh ? "条未读" : ""}</span>
        <button data-testid="mymentions-filter" onClick={() => setOnlyUnread((v) => !v)}
          className={`ml-auto text-xs px-3 py-1.5 rounded-full border ${onlyUnread ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-500 border-slate-200"}`}>
          {onlyUnread ? (zh ? "仅看未读完" : "Unread only") : (zh ? "查看全部" : "Show all")}
        </button>
        {totalUnread > 0 && (
          <Button data-testid="mymentions-nudge-all" size="sm" onClick={nudgeAll} className="bg-brand-500 hover:bg-brand-600 text-white text-xs">
            {zh ? `催全部未读 (${totalUnread})` : `Nudge all (${totalUnread})`}
          </Button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <CheckCircle2 className="h-10 w-10 text-green-300 mx-auto mb-3" />
          <p className="text-slate-400">{zh ? "太棒了，你 @ 的人都已查看！" : "All caught up — everyone you @'d has read."}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((r) => (
            <div key={r.id} data-testid={`mymention-${r.id}`} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800 break-words max-w-full sm:truncate sm:max-w-[340px]">{r.address}</span>
                    {r.phaseAtPost && <span className="text-[11px] bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">{r.phaseAtPost}</span>}
                    <span className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDT(r.createdAt)}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{r.text || (zh ? "（附件/链接）" : "(attachment/link)")}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(r.mentions || []).map((m) => {
                      const read = (r.readBy || []).includes(m);
                      return (
                        <span key={m} data-testid={`mymention-${r.id}-${m}`}
                          className={`text-[11px] rounded-full px-1.5 py-0.5 flex items-center gap-0.5 ${read ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                          @{nameOf(dir(m)) || m}{read ? <Check className="h-3 w-3" /> : <span className="text-[10px]">· {zh ? "待读" : "unread"}</span>}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <Button data-testid={`mymention-open-${r.id}`} variant="outline" size="sm" onClick={() => open(r)} className="text-xs flex-shrink-0">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />{zh ? "去处理" : "Open"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
